import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { passwordProblemMessage, type PasswordProblem } from '@wr/shared';
import { ApiError, errorMessage, post } from '../api.js';
import {
  NewPasswordFields,
  Notice,
  clearHash,
  newPasswordReady,
  tokenFromHash,
} from '../components/ui.js';
import { useLoaded } from '../session.js';
import { AuthLayout } from './AuthLayout.js';

/** Opened from the password reset email. */
export function ResetPassword() {
  const { rules, setSession } = useLoaded();
  const navigate = useNavigate();
  const [token] = useState(tokenFromHash);
  const [valid, setValid] = useState(false);
  const [failed, setFailed] = useState<string | null>(
    token ? null : errorMessage(new ApiError(400, 'invalid_token')),
  );
  const [value, setValue] = useState({ password: '', confirm: '' });
  const [serverProblems, setServerProblems] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    clearHash();
    if (!token) return;
    post('/auth/password/reset/check', { token })
      .then(() => setValid(true))
      .catch((err: unknown) => setFailed(errorMessage(err)));
  }, [token]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setServerProblems([]);
    try {
      await post('/auth/password/reset', { token, password: value.password });
      setSession({ state: 'signed_out' });
      navigate('/sign-in', {
        replace: true,
        state: {
          notice:
            'Your password was changed. Sign in with it, then with the code of your authenticator app.',
        },
      });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'weak_password') {
        const problems = (err.body.problems as PasswordProblem[] | undefined) ?? [];
        setServerProblems(problems.map((p) => passwordProblemMessage(p, rules.password)));
      } else {
        setError(errorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  }

  if (failed) {
    return (
      <AuthLayout title="Choose a new password">
        <Notice kind="error">{failed}</Notice>
        <p className="small">
          <Link to="/forgot-password">Ask for a new link</Link>
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Choose a new password">
      {error && <Notice kind="error">{error}</Notice>}
      <form onSubmit={(e) => void submit(e)} className="vstack">
        <NewPasswordFields
          policy={rules.password}
          personal={[]}
          value={value}
          onChange={setValue}
          serverProblems={serverProblems}
        />
        <button
          type="submit"
          className="btn primary block"
          disabled={busy || !valid || !newPasswordReady(value, rules.password, [])}
        >
          Change the password
        </button>
      </form>
    </AuthLayout>
  );
}
